import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import BaseDialog from './BaseDialog';
import { generateImage } from '../../utils/api';
import ImageConfigOptions from '../ImageConfigOptions';

// 定义TextToImageDialog的props接口
interface TextToImageDialogProps {
  isOpen: boolean;
  onClose: () => void;
  sceneId: string;
  sceneNumber: number;
  initialPrompt: string;
  initialResolution: string;
  initialQuality: string;
  imageSizes: string[];
  onUpdatePrompt: (sceneId: string, newPrompt: string) => void;
  onUpdateImages: (sceneId: string, images: string[]) => void;
  onUpdatePromptSilent?: (sceneId: string, newPrompt: string) => void; // 静默更新，不触发useEffect
  onUpdateResolution?: (resolution: string) => void;
  onUpdateQuality?: (quality: string) => void;
  onSetDialogOperating?: (sceneId: string, isOperating: boolean) => void; // 控制对话框操作状态
  onShowToast: (message: string) => void;
  currentImages: string[];
  sceneList?: Array<{ id: string; images: string[] }>; // 添加sceneList用于参考图
  onUpdateFirstImage?: (sceneId: string, imageUrl: string) => void; // 专门用于替换第一张图片
}

/**
 * 文生图对话框组件
 * - 继承BaseDialog的基础样式和布局
 * - 专门用于文本到图片的生成功能
 * - 包含提示词编辑、参数配置和图片预览
 */
const TextToImageDialog: React.FC<TextToImageDialogProps> = ({
  isOpen,
  onClose,
  sceneId,
  sceneNumber,
  initialPrompt,
  initialResolution,
  initialQuality,
  imageSizes,
  // onUpdatePrompt, // 现在使用静默更新
  onUpdateImages,
  onUpdatePromptSilent,
  onUpdateResolution,
  onUpdateQuality,
  onSetDialogOperating,
  onShowToast,
  currentImages,
  sceneList = [],
  onUpdateFirstImage
}) => {
  const { t } = useTranslation();
  const [prompt, setPrompt] = useState(initialPrompt);
  const [resolution, setResolution] = useState(initialResolution);
  const [quality, setQuality] = useState(initialQuality);
  const [showReferenceImages, setShowReferenceImages] = useState(false);
  const [selectedReferenceImages, setSelectedReferenceImages] = useState<string[]>([]);
  const [isGenerating, setIsGenerating] = useState(false); // 加载状态

  // 当对话框打开时，同步所有状态
  useEffect(() => {
    if (isOpen) {
      setPrompt(initialPrompt);
      setResolution(initialResolution);
      setQuality(initialQuality);
      setShowReferenceImages(false);
      setSelectedReferenceImages([]);
      setIsGenerating(false); // 重置加载状态
    }
  }, [isOpen, initialPrompt, initialResolution, initialQuality]);

  // 获取所有可用的参考图
  const getAllReferenceImages = () => {
    const images: Array<{ url: string; sceneId: string }> = [];
    sceneList.forEach(scene => {
      scene.images.forEach(imageUrl => {
        images.push({ url: imageUrl, sceneId: scene.id });
      });
    });
    return images;
  };

  // 切换参考图选择
  const toggleReferenceImage = (imageUrl: string) => {
    setSelectedReferenceImages(prev =>
      prev.includes(imageUrl)
        ? prev.filter(url => url !== imageUrl)
        : [...prev, imageUrl]
    );
  };

  // 处理生成图片
  const handleGenerateImage = async () => {
    // 设置加载状态
    setIsGenerating(true);

    // 创建请求参数
    const requestParams: any = {
      prompt: prompt,
      image_size: resolution.replace(/\s*\([^)]+\)$/, ''), // 移除括号里的比例信息
      num_images: 1, // 固定生成1张图片
      quality: quality,
      style: 'realistic',
      watermark: false
    };

    // 如果有参考图，添加image_urls参数
    if (selectedReferenceImages.length > 0) {
      requestParams.image_urls = selectedReferenceImages;
    }

    // 显示toast提示
    const message = `${t('toast.common.processing')}\n${t('dialog.textToImage.promptLabel')}: ${requestParams.prompt.slice(0, 20)}...\n${t('dialog.textToImage.resolutionLabel')}: ${requestParams.image_size}\n${t('dialog.textToImage.qualityLabel')}: ${requestParams.quality}`;
    onShowToast(message);

    console.log('🔥 [TextToImageDialog] 开始生成图片', { sceneId, prompt: requestParams.prompt.slice(0, 30) });

    // 设置对话框操作标志，阻止此分镜的自动生成
    if (onSetDialogOperating) {
      onSetDialogOperating(sceneId, true);
    }

    try {
      // 调用图片生成API
      console.log('🚀 [TextToImageDialog] 调用 generateImage API');
      const response = await generateImage(requestParams);

      if (response.code === 200 && response.data?.image_urls?.length > 0) {
        // 直接通过回调更新图片，避免触发useEffect
        onUpdateImages(sceneId, response.data.image_urls);

        // 静默更新提示词，不触发useEffect
        if (onUpdatePromptSilent) {
          onUpdatePromptSilent(sceneId, prompt);
        }

        // 显示成功提示
        onShowToast(t('toast.textToImage.generationSuccess'));

        // 可以选择是否自动关闭对话框
        // onClose();
      }
    } catch (error) {
      console.error('Image generation failed:', error);
      onShowToast(t('toast.textToImage.generationFailed'));
      // 生图失败时保持原有图片，不做任何更新
      // isGenerating状态会在finally中被重置为false
    } finally {
      // 清除加载状态
      setIsGenerating(false);
      // 无论成功还是失败，都重置对话框操作标志
      if (onSetDialogOperating) {
        onSetDialogOperating(sceneId, false);
      }
    }
  };

  return (
    <BaseDialog
      isOpen={isOpen}
      onClose={onClose}
      title={t('dialog.textToImage.title', { sceneNumber })}
      width={800}
      height={600}
    >
      <div className="space-y-4">
        {/* 提示词编辑区域 */}
        <div className="flex flex-col gap-2 relative">
          <label className="text-sm text-gray-400">{t('dialog.textToImage.promptLabel')}</label>
          <textarea
            className="p-4 bg-gray-900/80 rounded-lg border border-gray-700/50 whitespace-pre-wrap text-sm text-white resize-y min-h-[100px] pr-16"
            value={prompt}
            onChange={(e) => {
              const newPrompt = e.target.value;
              setPrompt(newPrompt);
              // 实时静默更新提示词到sceneList
              if (onUpdatePromptSilent) {
                onUpdatePromptSilent(sceneId, newPrompt);
              }
            }}
            placeholder={t('dialog.textToImage.promptPlaceholder')}
          />
          {/* 生成按钮 */}
          <button
            className="absolute bottom-2 right-2 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full text-white p-2 hover:shadow-lg transition-all transform hover:scale-105"
            onClick={handleGenerateImage}
            title="生成图片"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </button>
        </div>

        {/* 参考图选项 */}
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="reference-images"
              checked={showReferenceImages}
              onChange={(e) => setShowReferenceImages(e.target.checked)}
              className="rounded bg-gray-900/80 border-gray-700/50 text-blue-500 focus:ring-blue-500/50"
            />
            <label htmlFor="reference-images" className="text-sm text-gray-400 cursor-pointer">
              {t('dialog.textToImage.referenceImagesLabel')}
            </label>
            {selectedReferenceImages.length > 0 && (
              <span className="text-xs text-blue-400">
                {t('dialog.textToImage.selectedReferenceImages', { count: selectedReferenceImages.length })}
              </span>
            )}
          </div>

          {/* 参考图展示区域 */}
          {showReferenceImages && getAllReferenceImages().length > 0 && (
            <div className="bg-gray-900/40 rounded-lg p-3 border border-gray-700/30">
              <div className="text-xs text-gray-500 mb-2">{t('dialog.textToImage.referenceImagesInstruction')}</div>
              <div className="grid grid-cols-6 gap-2 max-h-40 overflow-y-auto">
                {getAllReferenceImages().map((image, index) => (
                  <div
                    key={index}
                    className={`relative aspect-square rounded cursor-pointer border-2 transition-all ${selectedReferenceImages.includes(image.url)
                      ? 'border-blue-500 ring-2 ring-blue-500/30'
                      : 'border-gray-700/50 hover:border-gray-600'
                      }`}
                    onClick={() => toggleReferenceImage(image.url)}
                  >
                    <img
                      src={image.url}
                      alt={`参考图 ${index + 1}`}
                      className="w-full h-full object-cover rounded"
                    />
                    {selectedReferenceImages.includes(image.url) && (
                      <div className="absolute top-1 right-1 w-4 h-4 bg-blue-500 rounded-full flex items-center justify-center">
                        <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {showReferenceImages && getAllReferenceImages().length === 0 && (
            <div className="bg-gray-900/40 rounded-lg p-3 border border-gray-700/30">
              <div className="text-xs text-gray-500">{t('dialog.textToImage.noReferenceImages')}</div>
            </div>
          )}
        </div>

        {/* 参数配置区域 */}
        <div className="space-y-4">
          {/* 使用ImageConfigOptions组件处理质量和分辨率 */}
          <ImageConfigOptions
            quality={quality}
            setQuality={(newQuality) => {
              setQuality(newQuality);
              if (onUpdateQuality) {
                onUpdateQuality(newQuality);
              }
            }}
            resolution={resolution}
            setResolution={(newResolution) => {
              setResolution(newResolution);
              if (onUpdateResolution) {
                onUpdateResolution(newResolution);
              }
            }}
            imageSizes={imageSizes}
          />


        </div>

        {/* 图片预览区域 */}
        <div className="flex flex-col gap-2 mt-4">
          <label className="text-sm text-gray-400">{t('dialog.textToImage.previewLabel')}</label>
          <div className="grid grid-cols-1 gap-3">
            {/* 只显示1个预览图，缩小25% */}
            <div
              className="bg-gray-800/80 rounded-lg overflow-hidden flex flex-col"
            >
              <div className="aspect-square flex items-center justify-center text-gray-400 hover:bg-gray-700/80 transition-colors overflow-hidden w-1/2 mx-auto relative">
                {isGenerating ? (
                  <div className="flex flex-col items-center justify-center absolute inset-0 bg-black/50 z-10">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mb-2"></div>

                  </div>
                ) : null}
                {currentImages[0] ? (
                  <img
                    src={currentImages[0]}
                    alt={t('dialog.textToImage.imagePlaceholder', { index: 1 })}
                    className="max-w-full max-h-full object-contain"
                  />
                ) : (
                  <span>{t('dialog.textToImage.imagePlaceholder', { index: 1 })}</span>
                )}
              </div>
              {/* 使用此图按钮 */}
              {currentImages[0] && (
                <button
                  className="bg-blue-500/80 hover:bg-blue-500 text-white text-xs py-1 px-2 transition-colors"
                  onClick={() => {
                    // 替换当前分镜的第一张图片
                    if (onUpdateFirstImage && sceneId && currentImages[0]) {
                      onUpdateFirstImage(sceneId, currentImages[0]);
                      onShowToast(t('toast.textToImage.imageReplaced', { index: 1 }));
                    }
                  }}
                >
                  {t('dialog.textToImage.useThisImage')}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </BaseDialog>
  );
};

export default TextToImageDialog;